import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { BASE_URL } from "../../../config/api";
import axios from "axios";

export default function BrandModal({ isOpen, onClose, onSubmit, initialData }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("1");
  const [logo, setLogo] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // ----------------- FETCH CATEGORIES -----------------
  const fetchCategories = async () => {

    try {

      const response =
        await axios.get(
          `${BASE_URL}warranty/get-categories`,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const data =
        response.data;

      // Keep only active categories
      const activeCats =
        data.filter(
          (cat) =>
            Number(
              cat.status
            ) === 1
        );

      setCategories(
        activeCats
      );

    } catch (err) {

      console.log(
        err
      );

    }
  };


  // ----------------- PREFILL (Edit Mode) -----------------
 useEffect(() => {
  const loadData = async () => {
    await fetchCategories(); // load categories first

    if (initialData) {
      setName(initialData.name || "");
      setDescription(initialData.description || "");
      setStatus(initialData.status?.toString() || "1");
      setLogo(initialData.image || "");

      // Extract category IDs from initialData.categories array
      const normalizedCats =
        (initialData.categories || []).map((c) => Number(c.id));

      setSelectedCats(normalizedCats);
    } else {
      setName("");
      setDescription("");
      setStatus("1");
      setLogo("");
      setSelectedCats([]);
    }
  };

  loadData();
}, [initialData]);

  if (!isOpen) return null;

  // ----------------- IMAGE UPLOAD -----------------
  const handleImageChange = async (
  e
) => {

  const file =
    e.target.files[0];

  if (!file)
    return;

  setImageUploading(
    true
  );

  try {

    const formData =
      new FormData();

    formData.append(
      "file",
      file
    );

    formData.append(
      "tag",
      "Brand"
    );

    const response =
      await axios.post(
        `${BASE_URL}warranty/upload-file`,
        formData,
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "multipart/form-data",
          },
        }
      );

    const data =
      response.data;

    if (
      data?.data
        ?.file_url
    ) {

      setLogo(
        data.data.file_url
      );
    }

  } catch (err) {

    console.error(
      err
    );

    alert(
      err?.response?.data
        ?.message ||
      "Image upload failed"
    );

  } finally {

    setImageUploading(
      false
    );

  }
};

  // ----------------- TOGGLE CATEGORY -----------------
  const toggleCategory = (id) => {
    if (selectedCats.includes(id)) {
      setSelectedCats(selectedCats.filter((c) => c !== id));
    } else {
      setSelectedCats([...selectedCats, id]);
    }
  };

  // ----------------- SUBMIT -----------------
  const handleSubmit = async (
  e
) => {

  e.preventDefault();

  setLoading(
    true
  );

  const payload = {

    name,

    description,

    image:
      logo,

    category_ids:
      selectedCats,

    status:
      Number(
        status
      ),
  };

  try {

    // Update Brand
    if (
      initialData?.id
    ) {

      await axios.post(
        `${BASE_URL}warranty/update-brand/${initialData.id}`,
        payload,
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",
          },
        }
      );
    }

    // Create Brand
    else {

      await axios.post(
        `${BASE_URL}warranty/create-brand`,
        payload,
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",
          },
        }
      );
    }

    onSubmit();

  } catch (err) {

    console.error(
      err
    );

    alert(
      err?.response?.data
        ?.message ||
      err?.message ||
      "Something went wrong"
    );

  } finally {

    setLoading(
      false
    );

  }
};

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl w-[480px] p-6 shadow-lg relative">

        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold mb-5">
          {initialData ? "Update Brand" : "Add New Brand"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Brand Name */}
          <div>
            <label className="block text-sm mb-1">Brand Name</label>
            <input
              type="text"
              value={name}
              required
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter brand name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Upload Logo */}
          <div>
            <label className="block text-sm mb-1">Upload Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="text-sm"
            />

            {logo && (
              <div className="mt-3">
                <img
                  src={logo}
                  alt="Preview"
                  className="w-20 h-20 object-cover border rounded"
                />
                {imageUploading && (
                  <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                )}
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const isSelected = selectedCats.includes(Number(cat.id));

              return (
                <span
                  key={cat.id}
                  onClick={() => toggleCategory(Number(cat.id))}
                  className={`px-3 py-1 text-xs rounded-full cursor-pointer border transition
                    ${isSelected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                    }
                  `}
                >
                  {cat.name}
                </span>
              );
            })}
          </div>


          {/* Status */}
          <div>
            <label className="block text-sm mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || imageUploading}
            className="w-full bg-blue-600 text-white py-2 rounded-md text-sm hover:bg-blue-700 font-semibold"
          >
            {loading ? "Saving..." : initialData ? "Update Brand" : "Add Brand"}
          </button>

        </form>
      </div>
    </div>
  );
}



// import React, { useEffect, useState } from "react";
// import { X } from "lucide-react";

// export default function BrandModal({ isOpen, onClose, onSubmit, initialData }) {
//   const [name, setName] = useState("");
//   const [description, setDescription] = useState("");
//   const [status, setStatus] = useState("1");
//   const [logo, setLogo] = useState("");
//   const [categories, setCategories] = useState([]);
//   const [selectedCats, setSelectedCats] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [imageUploading, setImageUploading] = useState(false);

//   // Load categories
//   const fetchCategories = async () => {
//     try {
//       const res = await fetch(
//         "https://goelectronix.in/api/v1/warranty/get-categories"
//       );
//       const data = await res.json();
//       setCategories(data);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   // Prefill if editing
//   useEffect(() => {
//     fetchCategories();

//     if (initialData) {
//       setName(initialData.name || "");
//       setDescription(initialData.description || "");
//       setStatus(initialData.status?.toString() || "1");
//       setLogo(initialData.image || "");
//       setSelectedCats(initialData.category_ids || []);
//     } else {
//       setName("");
//       setDescription("");
//       setStatus("1");
//       setLogo("");
//       setSelectedCats([]);
//     }
//   }, [initialData]);

//   if (!isOpen) return null;

//   // ----------------- Image Upload -----------------
//   const handleImageChange = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     setImageUploading(true);

//     try {
//       const formData = new FormData();
//       formData.append("file", file);
//       formData.append("tag", "Brand");

//       const res = await fetch(
//         "https://goelectronix.in/api/v1/warranty/upload-file",
//         {
//           method: "POST",
//           body: formData,
//         }
//       );

//       const data = await res.json();
//       if (data?.data?.file_url) {
//         setLogo(data.data.file_url);
//       }
//     } catch (err) {
//       alert("Image upload failed");
//     } finally {
//       setImageUploading(false);
//     }
//   };

//   // ----------------- Category Checkbox Handler -----------------
//   const toggleCategory = (id) => {
//     if (selectedCats.includes(id)) {
//       setSelectedCats(selectedCats.filter((c) => c !== id));
//     } else {
//       setSelectedCats([...selectedCats, id]);
//     }
//   };

//   // ----------------- Submit Handler -----------------
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     const payload = {
//       name,
//       description,
//       image: logo,
//       category_ids: selectedCats,
//       status: Number(status),
//     };

//     try {
//       // UPDATE
//       if (initialData?.id) {
//         payload.id = initialData.id;

//         const res = await fetch(
//           `https://goelectronix.in/api/v1/warranty/update-brand/${initialData.id}`,
//           {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify(payload),
//           }
//         );
//         if (!res.ok) throw new Error("Update failed");
//       }

//       // CREATE
//       else {
//         const res = await fetch(
//           "https://goelectronix.in/api/v1/warranty/create-brand",
//           {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify(payload),
//           }
//         );
//         if (!res.ok) throw new Error("Create failed");
//       }

//       onSubmit(); // refresh list
//     } catch (err) {
//       alert(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-xl w-[480px] p-6 shadow-lg relative">
//         {/* Close Button */}
//         <button
//           className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
//           onClick={onClose}
//         >
//           <X size={18} />
//         </button>

//         <h2 className="text-lg font-semibold mb-5">
//           {initialData ? "Update Brand" : "Add New Brand"}
//         </h2>

//         <form onSubmit={handleSubmit} className="space-y-4">
//           {/* Brand Name */}
//           <div>
//             <label className="block text-sm mb-1">Brand Name</label>
//             <input
//               type="text"
//               value={name}
//               required
//               onChange={(e) => setName(e.target.value)}
//               placeholder="Enter brand name"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
//             />
//           </div>

//           {/* Description */}
//           <div>
//             <label className="block text-sm mb-1">Description</label>
//             <input
//               type="text"
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               placeholder="Enter description"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
//             />
//           </div>

//           {/* Upload Logo */}
//           <div>
//             <label className="block text-sm mb-1">Upload Logo</label>
//             <input
//               type="file"
//               accept="image/*"
//               onChange={handleImageChange}
//               className="text-sm"
//             />

//             {logo && (
//               <div className="mt-3">
//                 <img
//                   src={logo}
//                   alt="Preview"
//                   className="w-20 h-20 object-cover border rounded"
//                 />
//                 {imageUploading && (
//                   <p className="text-xs text-gray-500 mt-1">Uploading...</p>
//                 )}
//               </div>
//             )}
//           </div>

//           {/* Select Categories */}
//           <div>
//             <label className="block text-sm mb-2">Select Categories</label>

//             <div className="grid grid-cols-2 gap-2">
//               {categories.map((cat) => (
//                 <label
//                   key={cat.id}
//                   className="flex items-center gap-2 text-sm"
//                 >
//                   <input
//                     type="checkbox"
//                     checked={selectedCats.includes(cat.id)}
//                     onChange={() => toggleCategory(cat.id)}
//                   />
//                   {cat.name}
//                 </label>
//               ))}
//             </div>
//           </div>

//           {/* Status */}
//           <div>
//             <label className="block text-sm mb-1">Status</label>
//             <select
//               value={status}
//               onChange={(e) => setStatus(e.target.value)}
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
//             >
//               <option value="1">Active</option>
//               <option value="0">Inactive</option>
//             </select>
//           </div>

//           {/* Submit Button */}
//           <button
//             type="submit"
//             disabled={loading || imageUploading}
//             className="w-full bg-blue-600 text-white py-2 rounded-md text-sm hover:bg-blue-700 font-semibold"
//           >
//             {loading ? "Saving..." : initialData ? "Update Brand" : "Add Brand"}
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// }
